import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardHeader from "@/components/DashboardHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Sparkles, FileText, Loader2 } from "lucide-react";
import {
  listMyCourses,
  formatPrice,
  coverBg,
  statusPill,
} from "@/lib/courses";

export default function TeacherDashboard() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listMyCourses()
      .then(setCourses)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="teacher-dashboard">
      <DashboardHeader subtitle="Teacher · My courses" />
      <main className="mx-auto max-w-6xl px-6 py-8 space-y-6">
        {/* Primary CTA — Build a new AI course */}
        <Link
          to="/teacher/courses/new"
          className="block"
          data-testid="primary-ai-course-cta"
        >
          <Card className="border-amber-300 bg-gradient-to-r from-amber-50 via-amber-100/60 to-amber-50 hover:shadow-lg transition-all hover:-translate-y-0.5">
            <CardContent className="py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="rounded-xl bg-amber-500 p-3 text-white shrink-0">
                  <Sparkles className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-lg font-semibold text-stone-900">
                    ✨ Build a new AI-assisted course
                  </div>
                  <p className="text-sm text-stone-700 mt-1">
                    Generate full chapter content — Classwork, Homework &amp;
                    Tests — from a single prompt.
                  </p>
                </div>
              </div>
              <Button
                size="lg"
                className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
              >
                Start the wizard →
              </Button>
            </CardContent>
          </Card>
        </Link>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">My courses</h1>
            <p className="text-sm text-stone-500">
              Author courses with AI, publish to students, archive when retired.
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/teacher/grading">
              <Button variant="outline" data-testid="teacher-grading-link">
                <Sparkles className="mr-2 h-4 w-4" /> Grading
              </Button>
            </Link>
            <Link to="/teacher/students">
              <Button variant="outline" data-testid="teacher-students-link">
                <Sparkles className="mr-2 h-4 w-4" /> My students
              </Button>
            </Link>
            <Link to="/calendar">
              <Button variant="outline" data-testid="teacher-calendar-link">
                <Sparkles className="mr-2 h-4 w-4" /> Calendar
              </Button>
            </Link>
            <Link to="/teacher/courses/new">
              <Button
                className="bg-stone-900 hover:bg-stone-800 text-white"
                data-testid="new-ai-course-button"
              >
                <Sparkles className="mr-2 h-4 w-4" /> New course (AI)
              </Button>
            </Link>
            <Link to="/teacher/courses/blank">
              <Button variant="outline" data-testid="new-blank-course-button">
                <Plus className="mr-2 h-4 w-4" /> Blank course
              </Button>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-stone-500" data-testid="courses-loading">
            <Loader2 className="mx-auto h-5 w-5 animate-spin mb-2" /> Loading your courses…
          </div>
        ) : courses.length === 0 ? (
          <Card className="border-dashed border-stone-300">
            <CardContent className="py-16 text-center text-stone-500" data-testid="no-courses">
              <FileText className="mx-auto h-6 w-6 mb-2" />
              No courses yet. Click <span className="font-medium">New course (AI)</span> to get started.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="course-list">
            {courses.map((c) => (
              <Link
                key={c.id}
                to={`/teacher/courses/${c.id}/edit`}
                className="block"
                data-testid={`course-card-${c.id}`}
              >
                <Card className="border-stone-200 hover:border-stone-400 transition-colors h-full">
                  <div className={`h-24 ${coverBg(c.cover_color)} border-b flex items-end px-4 pb-3`}>
                    <Badge variant="outline" className={statusPill(c.status)}>
                      {c.status}
                    </Badge>
                  </div>
                  <CardContent className="py-4 space-y-2">
                    <div className="font-semibold tracking-tight line-clamp-2">{c.title}</div>
                    {c.subtitle && (
                      <div className="text-xs text-stone-500 line-clamp-2">{c.subtitle}</div>
                    )}
                    <div className="flex items-center gap-2 text-xs text-stone-600 pt-1">
                      <Badge variant="outline">{c.level}</Badge>
                      <Badge variant="outline">{c.chapters.length} ch</Badge>
                      <Badge variant="outline" data-testid={`price-${c.id}`}>
                        {formatPrice(c.pricing)}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
